import React, { useState, useEffect } from "react";
import "./CreateUser.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { addCourse, getAllCategory, UploadFeatureImageUrl, uploadCourseVideo, UploadBlogBodyImage } from "proxyUrl";
import { BsFillImageFill } from "react-icons/bs";

const CourseAdd = () => {
  const [course, setCourse] = useState({
    title: "",
    slug: "",
    category: "",
    startDate: "",
    endDate: "",
  });
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submiteed, setSubmiteed] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  // Function to generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/[\s_-]+/g, "-") // Replace spaces, underscores, and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(getAllCategory, {
        withCredentials: true,
      });
      if (res.data && res.data.data) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    
    // Track if slug is manually edited
    if (name === "slug") {
      setSlugManuallyEdited(true);
    }
    
    setCourse((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Auto-generate slug from title when title changes (only if slug wasn't manually edited)
  useEffect(() => {
    if (course.title && !slugManuallyEdited) {
      const autoSlug = generateSlug(course.title);
      setCourse((prev) => ({
        ...prev,
        slug: autoSlug,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.title]);

  const handleImageChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setImage(selectedFile);
      setImagePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleVideoChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setVideo(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmiteed(true);

    if (!course.title || !course.slug || !course.category || !description) {
      toast.error("Please fill all fields!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setSubmiteed(false);
      return;
    }

    if (!image) {
      toast.error("Please upload an image!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setSubmiteed(false);
      return;
    }

    if (!video) {
      toast.error("Please upload a video!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setSubmiteed(false);
      return;
    }

    try {
      // Upload image
      const imageFormData = new FormData();
      imageFormData.append("image", image);
      const imageRes = await axios.post(UploadFeatureImageUrl, imageFormData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Upload video
      const videoFormData = new FormData();
      videoFormData.append("video", video);
      const videoRes = await axios.post(uploadCourseVideo, videoFormData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Create course with uploaded image and video URLs
      const courseRes = await axios.post(
        addCourse,
        {
          title: course.title,
          slug: course.slug,
          category: course.category,
          description: description,
          image: imageRes.data.imageUrl,
          video: videoRes.data.videoUrl,
          status: course.status,
          start_date: course.startDate,
          end_date: course.endDate,
        },
        {
          withCredentials: true,
        }
      );

      if (courseRes.status === 200) {
        toast.success("Course created successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setTimeout(() => {
          navigate("/admin/parents-arena-course");
        }, 2200);
      } else {
        setSubmiteed(false);
      }
    } catch (err) {
      console.log(err);
      toast.error("Something went wrong!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setSubmiteed(false);
    }
  };

  return (
    <>
      <div className="mt-7 rounded-2xl py-4 dark:bg-navy-800">
        <form
          className="create-user-form bg-white dark:bg-navy-800"
          onSubmit={handleSubmit}
        >
          <div className="create-form-wrapper" style={{ width: '100%' }}>
            <div className="field">
              <div className="input">
                <label
                  htmlFor="title"
                  className="text-navy-900 dark:text-white"
                >
                  Title
                </label>
                <input
                  type="text"
                  placeholder="Course title"
                  id="title"
                  name="title"
                  value={course.title}
                  onChange={handleInput}
                />
              </div>
              <div className="input">
                <label
                  htmlFor="slug"
                  className="text-navy-900 dark:text-white"
                >
                  Slug
                </label>
                <input
                  type="text"
                  placeholder="course-slug"
                  id="slug"
                  name="slug"
                  value={course.slug}
                  onChange={handleInput}
                />
              </div>
              <div className="input">
                <label
                  htmlFor="category-select"
                  className="text-navy-900 dark:text-white"
                >
                  Category
                </label>
                <select
                  id="category-select"
                  name="category"
                  value={course.category}
                  onChange={handleInput}
                  className="mt-2"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <div className="input">
                <label
                  htmlFor="category-select"
                  className="text-navy-900 dark:text-white"
                >
                  Status
                </label>
                <select
                  id="category-select"
                  name="status"
                  value={course.status}
                  onChange={handleInput}
                  className="mt-2"
                >
                  <option value="">Select Status</option>
                  <option value="1">Active</option>
                  <option value="0">Disabled</option>
                </select>
              </div>
              <div className="input">
                <label
                  htmlFor="startDate"
                  className="text-navy-900 dark:text-white"
                >
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={course.startDate}
                  onChange={handleInput}
                />
              </div>
              <div className="input">
                <label
                  htmlFor="endDate"
                  className="text-navy-900 dark:text-white"
                >
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={course.endDate}
                  onChange={handleInput}
                />
              </div>
            </div>
            <div className="field">
              <div className="input relative mx-2 mr-7 flex items-center justify-center rounded-lg border-2 border-blueSecondary">
                <div className="h-[200px] w-[400px] rounded-lg">
                  {imagePreview ? (
                    <img
                      className="h-full w-full rounded-md object-cover"
                      src={imagePreview}
                      alt="Preview"
                    />
                  ) : (
                    <div className="w-full h-full text-8xl text-primaryBlue flex items-center justify-center">
                      <BsFillImageFill />
                    </div>
                  )}
                </div>
              </div>
              <div className="input relative">
                <label
                  htmlFor="image"
                  className="text-navy-900 dark:text-white"
                >
                  Upload Image
                </label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
              <div className="input relative">
                <label
                  htmlFor="video"
                  className="text-navy-900 dark:text-white"
                >
                  Upload Video
                </label>
                <input
                  type="file"
                  id="video"
                  name="video"
                  accept="video/*"
                  onChange={handleVideoChange}
                />
              </div>
            </div>

            <div className="relative mx-2">
              <h2 className="mb-1 text-navy-900 dark:text-white">Description</h2>
              <CKEditor
                value={description}
                editor={ClassicEditor}
                onInit={(editor) => {
                  // Editor is ready to be used
                }}
                onChange={(handleInput, editor) => {
                  const data = editor.getData();
                  setDescription(data);
                }}
                config={{
                  ckfinder: {
                    uploadUrl: `${UploadBlogBodyImage}`,
                    withCredentials: true,
                  },
                }}
              />
            </div>

            <div className="bg-transperent mx-2 mt-5 flex items-center justify-center">
              {submiteed ? (
                <div className="field cursor-pointer">
                  <div className="my-5 rounded-md bg-gray-300 py-3 px-12 text-white">
                    <button disabled>Loading....</button>
                  </div>
                </div>
              ) : (
                <div className="field cursor-pointer">
                  <div className="my-5 rounded-md bg-primaryBlue py-3 px-12 text-white">
                    <button type="submit">Submit</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
      <ToastContainer />
    </>
  );
};

export default CourseAdd;
